import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import type { User } from '@core/account-types';
import { CandidateBulkActions, FullCandidateActions } from '@core/candidate-data';
import type { FullCandidate } from '@core/candidate-types';
import { CandidateSharedWithListComponent } from '@core/candidate-ui';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { ProsemirrorBasicConfigDirective, WysiwygComponent } from '@core/prosemirror-legacy/webapp';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  IconComponent,
  LabelComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

export interface CandidateSharingConfirmDialogData {
  assignments: User[];
  message?: Record<string, unknown>;
  candidates: FullCandidate[];
  offerId: number;
  isBulk: boolean;
}

export interface CandidateSharingConfirmDialogResult {
  goBack: boolean;
  message: Record<string, unknown>;
}

@Component({
  selector: 'rt-candidate-sharing-confirm-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'candidate-sharing-confirm-dialog.component.html',
  styleUrls: ['./candidate-sharing-confirm-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    ButtonComponent,
    TranslatePipe,
    IconComponent,
    ReactiveFormsModule,
    LabelComponent,
    BadgeComponent,
    CandidateSharedWithListComponent,
    WysiwygComponent,
    ProsemirrorBasicConfigDirective,
  ],
})
export class CandidateSharingConfirmDialogComponent implements DialogComponentI<
  CandidateSharingConfirmDialogData,
  CandidateSharingConfirmDialogResult | null | undefined
> {
  private readonly dialogRef = inject(DialogRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);

  public assignments: User[] = [];
  public formControl: FormControl = new FormControl<Record<string, unknown> | null>(null);
  public candidates: FullCandidate[];
  public offerId: number;
  public heading: string;
  private isBulk: boolean;

  public setDialogData(data: CandidateSharingConfirmDialogData, triggerUpdate: boolean): void {
    this.assignments = data.assignments ?? [];
    this.formControl.setValue(data.message ?? null);
    this.candidates = data.candidates;
    this.offerId = data.offerId;
    this.heading =
      this.candidates.length > 1
        ? this.i18n.translate(
            'app.candidate.admin_select.share_with_team.confirm_dialog.heading_multiple',
            { count: this.candidates.length },
          )
        : this.i18n.translate('app.candidate.admin_select.share_with_team.confirm_dialog.heading', {
            candidateName: this.candidates[0].name,
          });
    this.isBulk = data.isBulk;

    if (triggerUpdate) {
      this.cdRef.detectChanges();
    }
  }

  public goBack(): void {
    this.dialogRef.close({ goBack: true, message: this.formControl.value });
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public share(): void {
    if (this.isBulk) {
      this.bulkShare();
    } else {
      this.singleShare();
    }

    this.dialogRef.close({
      goBack: false,
      message: this.formControl.value,
    });
  }

  private singleShare(): void {
    this.store.dispatch(
      FullCandidateActions.shareWithTeam({
        payload: {
          candidateId: this.candidates[0].id,
          admins: this.candidates[0].admins || [],
          message: this.formControl.value,
          placementId: this.selectedOfferPlacementIds()[0],
          adminIds: this.assignments.map(user => user.id),
        },
      }),
    );
  }

  private bulkShare(): void {
    this.store.dispatch(
      CandidateBulkActions.shareWithTeam({
        payload: {
          correlationId: generateUuid(),
          params: {
            adminIds: this.assignments.map(user => user.id),
            placementIds: this.selectedOfferPlacementIds(),
            message: this.formControl.value,
          },
        },
      }),
    );
  }

  private selectedOfferPlacementIds(): number[] {
    return this.candidates.flatMap(candidate =>
      candidate.placements
        .filter(placement => (placement.offer || placement.talentPool)?.id === this.offerId)
        .map(placement => placement.id),
    );
  }
}
